import { publishGenerationStatus } from 'Pods/Shared/GenerationStatus/actions'

import { GENERATION_PHASE } from '../constants'
import { threeDStudioStore } from '../index'

// Client-side phase ordering used to guard monotonicity: an out-of-order broadcast
// or a connect-time reconcile must never walk the progress UI backwards.
const PHASE_ORDER = [
  GENERATION_PHASE.QUEUED,
  GENERATION_PHASE.ANALYZING,
  GENERATION_PHASE.GENERATING,
  GENERATION_PHASE.REFINING,
  GENERATION_PHASE.FINALIZING,
]

const setGenerationState = (patch) => {
  threeDStudioStore.setState((state) => ({
    generation: { ...state.generation, ...patch },
  }))
  // Mirror live status into the global store so any pack (top nav, etc.) can read it.
  publishGenerationStatus(patch)
}

// The single-run scalar fields (isGenerating, runId, ...) predate concurrent
// generations and are still read by single-run consumers (viewer banner, collapsed
// card, the global GenerationStatus mirror). They track the most recently started
// in-flight run; with no runs left they reset to their defaults.
const scalarRunFieldsFor = (run) => (run
  ? {
    isGenerating: true,
    isEnhancing: run.enhancingSessionId != null,
    triggeredBy: run.triggeredBy ?? null,
    enhancingSessionId: run.enhancingSessionId,
    generationStartedAt: run.startedAt ?? null,
    runId: run.runId,
    sessionId: run.sessionId,
    lastGenerationSettings: run.settings ?? {},
  }
  : {
    isGenerating: false,
    isEnhancing: false,
    triggeredBy: null,
    enhancingSessionId: undefined,
    generationStartedAt: null,
    runId: undefined,
    sessionId: undefined,
    lastGenerationSettings: {},
  })

/**
 * Returns the generation-slice fields that must survive a slice reset while runs
 * are in flight: the run list itself, the scalar mirror of its latest run, and the
 * live wallet balance. Spread over DEFAULT_GENERATION when resetting the slice.
 * @param {object} generation - The current generation slice
 */
export const activeRunStateOf = (generation) => ({
  activeRuns: generation.activeRuns,
  availableCredits: generation.availableCredits,
  ...scalarRunFieldsFor(generation.activeRuns[generation.activeRuns.length - 1]),
})

/**
 * Registers a newly started (or restored) in-flight run and points the single-run
 * scalar fields at it. Starting a run also clears any previous failure state.
 * @param {object} run - { clientId, runId?, sessionId?, triggeredBy, isEnhancement,
 *   enhancingSessionId?, startedAt, settings? }
 */
export const addActiveRun = (run) => {
  // POST /runs carries no phase, so every new (or restored) run starts as queued;
  // the run's channel then advances it via updateActiveRunByRunId.
  const seededRun = { phase: GENERATION_PHASE.QUEUED, ...run }
  let patch = {}
  threeDStudioStore.setState((state) => {
    patch = {
      activeRuns: [...state.generation.activeRuns, seededRun],
      ...scalarRunFieldsFor(seededRun),
      hasFailed: false,
      errorDetails: {},
      errorId: '',
    }
    return { generation: { ...state.generation, ...patch } }
  })
  publishGenerationStatus(patch)
}

/**
 * Merges a patch into one in-flight run (matched by clientId). When the patched run
 * is the latest one, the single-run scalar fields are refreshed from it too.
 * @param {string} clientId - The run's client-generated id
 * @param {object} runPatch - Fields to merge into the run (runId, sessionId, settings)
 */
export const updateActiveRun = (clientId, runPatch) => {
  let patch = {}
  threeDStudioStore.setState((state) => {
    const activeRuns = state.generation.activeRuns.map((run) => (
      run.clientId === clientId ? { ...run, ...runPatch } : run
    ))
    const latest = activeRuns[activeRuns.length - 1]
    patch = {
      activeRuns,
      ...(latest?.clientId === clientId ? scalarRunFieldsFor(latest) : {}),
    }
    return { generation: { ...state.generation, ...patch } }
  })
  publishGenerationStatus(patch)
}

// Applies runPatch to one run, dropping a phase (and its timestamp) that would move
// the run backwards in PHASE_ORDER. A same-rank phase still applies so a reconcile
// can refresh phaseUpdatedAt with the server's anchor.
const withGuardedPhase = (run, runPatch) => {
  const { phase, phaseUpdatedAt, ...rest } = runPatch
  if (phase == null) {
    return { ...run, ...rest }
  }

  const incomingRank = PHASE_ORDER.indexOf(phase)
  const currentRank = PHASE_ORDER.indexOf(run.phase)
  if (incomingRank < 0 || incomingRank < currentRank) {
    return { ...run, ...rest }
  }

  return { ...run, ...rest, phase, ...(phaseUpdatedAt != null ? { phaseUpdatedAt } : {}) }
}

/**
 * Merges a live-progress patch into one in-flight run, matched by its backend runId
 * (updateActiveRun matches clientId, which channel messages do not carry). Phase
 * transitions are kept monotonic; see withGuardedPhase.
 * @param {number|string} runId - The run's backend id
 * @param {object} runPatch - Fields to merge (phase, phaseUpdatedAt, queuePosition, queueSize)
 */
export const updateActiveRunByRunId = (runId, runPatch) => {
  let patch = {}
  threeDStudioStore.setState((state) => {
    const activeRuns = state.generation.activeRuns.map((run) => (
      String(run.runId) === String(runId) ? withGuardedPhase(run, runPatch) : run
    ))
    const latest = activeRuns[activeRuns.length - 1]
    patch = {
      activeRuns,
      ...(String(latest?.runId) === String(runId) ? scalarRunFieldsFor(latest) : {}),
    }
    return { generation: { ...state.generation, ...patch } }
  })
  publishGenerationStatus(patch)
}

/**
 * Removes a settled (completed/failed/rejected) run and re-points the single-run
 * scalar fields at the most recent remaining run, if any.
 * @param {{ clientId?: string, runId?: number|string }} key - Matched by clientId
 *   when given, otherwise by runId
 */
export const removeActiveRun = ({ clientId, runId }) => {
  let patch = {}
  threeDStudioStore.setState((state) => {
    const activeRuns = state.generation.activeRuns.filter((run) => (
      clientId != null
        ? run.clientId !== clientId
        : String(run.runId) !== String(runId)
    ))
    patch = {
      activeRuns,
      ...scalarRunFieldsFor(activeRuns[activeRuns.length - 1]),
    }
    return { generation: { ...state.generation, ...patch } }
  })
  publishGenerationStatus(patch)
}

/**
 * Sets whether a generation is in progress
 * @param {boolean} isGenerating - True when generation is running
 * @param {number|string|undefined} generationStartedAt - Timestamp or date string when generation started
 */
export const setIsGenerating = (isGenerating, generationStartedAt) => {
  if (isGenerating && generationStartedAt) {
    const date = new Date(generationStartedAt)
    const dateInMilliseconds = date.getTime()
    setGenerationState({ isGenerating, generationStartedAt: dateInMilliseconds })

    return
  }

  setGenerationState({
    isGenerating,
    ...(isGenerating
      ? { hasFailed: false, errorDetails: {}, errorId: '' }
      : { isEnhancing: false, triggeredBy: null }),
    generationStartedAt: isGenerating ? Date.now() : null,
  })
}

/**
 * Marks whether the in-flight generation is an enhancement (remesh/retexture/
 * segment) rather than an initial generation. Drives the "Processing..." sidebar
 * state. Cleared automatically when generation ends (see setIsGenerating).
 * @param {boolean} isEnhancing - True when an enhancement run is in progress
 */
export const setIsEnhancing = (isEnhancing) => {
  setGenerationState({ isEnhancing })
}

/**
 * Records which action button started the in-flight run so only that button shows
 * the in-progress label; every other action button is disabled with an "in
 * progress" tooltip. Cleared when generation ends (see setIsGenerating).
 * @param {string|null} triggeredBy - The triggering action key (GENERATION_TRIGGER_KEY
 *   for an initial generation, or the enhancement tab id for an enhancement run)
 */
export const setTriggeredBy = (triggeredBy) => {
  setGenerationState({ triggeredBy })
}

/**
 * Sets the session id of the model whose enhancement run is in progress. Drives
 * the "Processing..." card for that session in the library. On reload the value is
 * re-derived from the pending-runs response (see usePendingGeneratorRun).
 * @param {number|string|undefined} enhancingSessionId - The enhanced model's session id
 */
export const setEnhancingSessionId = (enhancingSessionId) => {
  setGenerationState({ enhancingSessionId })
}

/**
 * Sets whether a generation has completed at least once
 * @param {boolean} hasGenerated - True after the first successful generation
 */
export const setHasGenerated = (hasGenerated) => {
  setGenerationState({ hasGenerated })
}

/**
 * Sets the current generation run id
 * @param {number|undefined} runId - The run id from the current generation
 */
export const setRunId = (runId) => {
  setGenerationState({ runId })
}

/**
 * Sets the current generation session id
 * @param {number|undefined} sessionId - The session id from the current generation
 */
export const setSessionId = (sessionId) => {
  setGenerationState({ sessionId })
}

/**
 * Sets the last generation settings
 * @param {object} lastGenerationSettings - The last generation settings
 */
export const setLastGenerationSettings = (lastGenerationSettings) => {
  setGenerationState({ lastGenerationSettings })
}

/**
 * Sets the available credits
 * @param {number} availableCredits - The available credits
 */
export const setAvailableCredits = (availableCredits) => {
  setGenerationState({ availableCredits })
}

/**
 * Snapshots the Generate panel settings the run being started uses. The Generate
 * button stays disabled while the panel still matches the snapshot (see
 * selectIsGenerationLocked), so a second click cannot resubmit the same generation.
 */
export const lockGenerationSettings = () => {
  const { modelSettings, schema } = threeDStudioStore.getState()
  setGenerationState({
    lockedGenerationSettings: {
      modelSettings: { ...modelSettings },
      generationAction: schema.generationAction,
      generationProvider: schema.generationProvider,
    },
  })
}

/**
 * Drops the locked settings snapshot so the Generate button is enabled again
 * regardless of whether the panel changed (failed run, user left the page).
 */
export const unlockGenerationSettings = () => {
  setGenerationState({ lockedGenerationSettings: null })
}

/**
 * Sets the generation has failed. A failed run is no longer "in progress", so the
 * same-settings lock is released with it and the retry button works again.
 * @param {boolean} hasFailed - True when generation has failed
 */
export const setHasFailed = (hasFailed) => {
  setGenerationState({ hasFailed, ...(hasFailed && { lockedGenerationSettings: null }) })
}

/**
 * Sets the generation error id
 * @param {string} errorId - The error id from the current generation
 */
export const setErrorId = (errorId) => {
  setGenerationState({ errorId })
}

/**
 * Sets the generation error details (failureReason, failureMessage, providerErrorCode)
 * @param {object} errorDetails - The structured error payload from the backend
 */
export const setErrorDetails = (errorDetails) => {
  setGenerationState({ errorDetails: errorDetails || {} })
}
